// Storage for a page's `local` navigation parameters — the values that are too
// big or too complex to travel in the address bar, so only an id for them goes
// into the address and the values themselves are parked here.
//
// There are two stores with different rules, and `hydrateLocalParams` picks
// between them:
//  - callable pages use an in-memory map, keyed by the call id. Their params may
//    contain functions, so they are never serialised, and they are dropped on a
//    browser refresh. Hydration copies EVERY key back onto the page's params.
//  - ordinary pages use sessionStorage, keyed by a generated local-param id.
//    Hydration copies back only `local`.
//
// Kept out of nav-controller.ts so these rules can be unit-tested. They are pure
// module-level functions with no Angular dependency, but nav-controller.ts
// imports @angular/core, and the unit-test runner (mocha under ts-node, in Node)
// cannot load a file that transitively pulls in @angular/core's ESM exports —
// the same constraint recorded in cs-day-items-changelog-card.ts. Tests live in
// nav-local-params.test.ts.
//
// sessionStorage is deliberately read as a global at call time rather than
// injected, so the move out of nav-controller.ts changed no signature and no
// call site. A test installs an in-memory shim on globalThis before calling in.
//
// The wider picture — what `local` params are for and when not to use them — is
// in design-docs/navigation.md.

export const LOCAL_STORAGE_PREFIX = 'nav_local_';
// keep the most recent 50 entries — evict oldest when storing a new one.
// count-based (not time-based) so entries are never deleted while the page is still
// in the nav stack. sessionStorage is tab-scoped and clears itself on tab close,
// so unbounded growth is not a concern. (Sentry 3QW: old 30-min timer deleted
// entries for pages users were still actively viewing.)
export const LOCAL_STORAGE_MAX_ENTRIES = 50;

interface LocalParamEntry {
  data: any;
  timestamp: number;
  pageId: string; // Used to associate with a specific navigation entry
}

/**
 * The two ways a stored entry turns out to be damaged. Nothing but this module writes these
 * entries, so an entry that cannot be parsed is damage, not an old format. The values are the
 * report names.
 */
export enum LocalParamDamage {
  // a page asked for its params and got none: still broken when this is reported
  Unreadable = 'NavLocalParamDamaged',
  // the eviction pass found one and removed it: already repaired when this is reported
  Evicted = 'NavLocalParamDamagedEvicted',
}

/** Hands on a damaged entry. See NavLocalParamsSettings.reportDamage. */
export type LocalParamDamageReporter = (damage: LocalParamDamage, err: unknown) => void;

// #6493. The count cap above never looks at size, and an entry stays after its page closes.
// Edit Workouts hands every Exercise Switch and Exercise Configuration screen a full copy of the
// plan and training state (about 110 KB for the reporter), so about 25 such screens filled the
// tab's sessionStorage and every later store failed until the app restarted. So the entries of
// CLOSED pages are also kept under this many characters in total, oldest going first, and a
// store that still hits the quota frees every closed-page entry and tries once more. Entries
// of pages still on the stack are never removed: a reload or warm resume needs them.
export const CLOSED_PAGE_ENTRIES_BUDGET_CHARS = 1_000_000;

// an entry this young is never treated as closed. Its page may simply not have reached the
// stack yet: a push stores its params first and adds the page after an await.
export const RECENT_ENTRY_GRACE_MS = 10_000;

/** What a quota recovery freed, for the report. */
export interface LocalParamQuotaRecovery {
  entriesFreed: number;
  charsFreed: number;
}

/**
 * Answers the localIds of the pages currently on the stack, or undefined when that cannot be
 * known (no navigation component). See NavLocalParamsSettings.openLocalParamIds.
 */
export type OpenLocalParamIdsProvider = () => ReadonlySet<string> | undefined;

/**
 * What this module calls out to. This module is Angular-free so it can be unit-tested, and
 * several of its callers (cs-nav among them) have no logger to pass in, which is why these are
 * module-level settings rather than arguments. NavController supplies them through
 * configureNavLocalParams at app start.
 */
export interface NavLocalParamsSettings {
  // where damaged entries are reported. Set by NavController.setLogger; until then damage is
  // only handled, not reported.
  reportDamage: LocalParamDamageReporter;
  // which entries belong to pages on the stack. Set by NavController's constructor. Until it is
  // set, or while it answers undefined, every entry counts as open, so nothing is removed for
  // being closed: the safe direction.
  openLocalParamIds: OpenLocalParamIdsProvider;
  // where a store that recovered from a full sessionStorage is reported. Set by
  // NavController.setLogger.
  reportQuotaRecovery: (recovery: LocalParamQuotaRecovery) => void;
  // the clock the grace period reads. Only tests replace it.
  now: () => number;
}

const DEFAULT_SETTINGS: Readonly<NavLocalParamsSettings> = {
  reportDamage: () => { /* no reporter registered yet */ },
  openLocalParamIds: () => undefined,
  reportQuotaRecovery: () => { /* no reporter registered yet */ },
  now: () => Date.now(),
};

let settings: NavLocalParamsSettings = { ...DEFAULT_SETTINGS };

/**
 * Supply some of this module's collaborators. A key left out keeps its current value; a key
 * given as `undefined` goes back to its default, which is how a test unregisters one.
 */
export function configureNavLocalParams(partial: Partial<NavLocalParamsSettings>): void {
  // if the key was given, take it (undefined meaning the default), else keep what is set
  function pick<K extends keyof NavLocalParamsSettings>(key: K): NavLocalParamsSettings[K] {
    if (!(key in partial)) return settings[key];
    // TypeScript cannot see that `?? default` removes the undefined for a generic key
    return (partial[key] ?? DEFAULT_SETTINGS[key]) as NavLocalParamsSettings[K];
  }
  settings = {
    reportDamage: pick('reportDamage'),
    openLocalParamIds: pick('openLocalParamIds'),
    reportQuotaRecovery: pick('reportQuotaRecovery'),
    now: pick('now'),
  };
}

/**
 * Put every collaborator back to its default in one call, so a test cannot leave one behind
 * for the tests after it.
 */
export function resetNavLocalParams(): void {
  settings = { ...DEFAULT_SETTINGS };
}

/**
 * The localIds of the pages on a navigation stack, for an OpenLocalParamIdsProvider. Answers
 * undefined ("cannot know") when there is no stack, when it is empty, or while it is being
 * rebuilt page by page from an address: the pages not yet pushed are open too, and their
 * entries must survive until they are.
 */
export function openLocalParamIdsOfStack(
  pages: readonly ({ data?: any } | undefined)[] | undefined,
  stackBeingRebuilt: boolean,
): ReadonlySet<string> | undefined {
  if (!pages || pages.length === 0 || stackBeingRebuilt) return undefined;
  const openIds = new Set<string>();
  for (const page of pages) {
    const localId = page?.data?.localId;
    if (typeof localId === 'string') openIds.add(localId);
  }
  return openIds;
}

/**
 * In-memory store for ephemeral local params (used by callable pages).
 * These params can contain functions and are not persisted to sessionStorage.
 */
export const ephemeralLocalParams = new Map<string, any>();

/**
 * Generate a unique ID for local parameter storage.
 */
export function generateLocalParamId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Get the sessionStorage key for a local param ID.
 */
export function getStorageKey(localParamId: string): string {
  return `${LOCAL_STORAGE_PREFIX}${localParamId}`;
}

/**
 * Store local parameter data in sessionStorage.
 * Returns the generated ID that should be stored in the URL.
 * Throws if the data contains functions or circular references.
 *
 * @param data - The data to store
 * @param pageId - The page ID for cleanup tracking
 */
export function storeLocalParam(data: any, pageId: string): string {
  const localParamId = generateLocalParamId();

  // validate - no functions allowed in sessionStorage
  if (typeof data === 'function') {
    throw new Error(
      `NavController: local parameter contains a function. ` +
      `Functions cannot be stored. Use event emitters or services for callbacks.`
    );
  }

  // evict oldest entries if at capacity, and closed pages' entries if over their budget
  evictOldestLocalParams();

  const entry: LocalParamEntry = {
    data,
    timestamp: settings.now(),
    pageId
  };

  // Single JSON.stringify - catches circular refs and other non-serializable data
  let serialized: string;
  try {
    serialized = JSON.stringify(entry);
  } catch (error) {
    throw new Error(
      `NavController: local parameter contains non-serializable data. ` +
      `Circular references cannot be stored. Error: ${error.message}`
    );
  }

  try {
    writeWithQuotaRecovery(getStorageKey(localParamId), serialized);
  } catch (error) {
    console.error('NavController: Failed to store local param in sessionStorage:', error);
    throw new Error(`NavController: Failed to store local parameter: ${error.message}`);
  }

  return localParamId;
}

/**
 * Write one entry. If sessionStorage is full, free every closed page's entry and try once more.
 * Throws whatever the last attempt threw.
 */
function writeWithQuotaRecovery(storageKey: string, serialized: string): void {
  try {
    sessionStorage.setItem(storageKey, serialized);
    return;
  } catch (error) {
    if (!isQuotaExceededError(error)) throw error;
    const recovery = removeClosedPageEntries(readStoredEntries(), 0);
    // if nothing could be freed, the retry would fail the same way
    if (recovery.entriesFreed === 0) throw error;
    sessionStorage.setItem(storageKey, serialized);
    // the store went through, so this is a repair that already ran
    settings.reportQuotaRecovery(recovery);
  }
}

/**
 * True for the error a browser throws when sessionStorage is full. WebKit and Chromium name it
 * QuotaExceededError (legacy code 22); older Firefox used NS_ERROR_DOM_QUOTA_REACHED (code 1014).
 */
function isQuotaExceededError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { name, code } = error as { name?: unknown; code?: unknown };
  return name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED' || code === 22 || code === 1014;
}

/**
 * Remove closed pages' entries, oldest first, until those left total at most `budgetChars`.
 * An entry counts as closed only when the provider can say which pages are open, the entry is
 * not one of them, and it is older than RECENT_ENTRY_GRACE_MS.
 */
function removeClosedPageEntries(entries: StoredEntrySummary[], budgetChars: number): LocalParamQuotaRecovery {
  const recovery: LocalParamQuotaRecovery = { entriesFreed: 0, charsFreed: 0 };
  const openIds = settings.openLocalParamIds();
  if (!openIds) return recovery;

  const graceCutoff = settings.now() - RECENT_ENTRY_GRACE_MS;
  const closed = entries.filter(entry =>
    !openIds.has(entry.key.substring(LOCAL_STORAGE_PREFIX.length)) &&
    // a missing or wrong-typed timestamp cannot prove the entry is old, so it is kept
    typeof entry.timestamp === 'number' && entry.timestamp <= graceCutoff);

  let closedChars = closed.reduce((sum, entry) => sum + entry.chars, 0);
  closed.sort((a, b) => a.timestamp - b.timestamp);
  for (const entry of closed) {
    if (closedChars <= budgetChars) break;
    sessionStorage.removeItem(entry.key);
    closedChars -= entry.chars;
    recovery.entriesFreed++;
    recovery.charsFreed += entry.chars;
  }
  return recovery;
}

/**
 * Retrieve local parameter data from sessionStorage.
 */
export function retrieveLocalParam(localParamId: string): any {
  const storageKey = getStorageKey(localParamId);
  const stored = sessionStorage.getItem(storageKey);

  if (!stored) {
    console.warn(`NavController: Local param ${localParamId} not found in sessionStorage`);
    return undefined;
  }

  try {
    const entry: LocalParamEntry = JSON.parse(stored);
    return entry.data;
  } catch (error) {
    // the page opens without its local params
    settings.reportDamage(LocalParamDamage.Unreadable, error);
    return undefined;
  }
}

/**
 * Hydrate params on a navParams data object.
 * For callable pages: copies ALL params from ephemeral memory (so data.key works like pushAsync).
 * For regular pages: copies only data.local from sessionStorage.
 */
export function hydrateLocalParams(data: any): void {
  if (!data || typeof data !== 'object') return;

  // for ephemeral callable pages (in-memory storage) - hydrate ALL params
  const callId = data._callId;
  if (callId && ephemeralLocalParams.has(callId)) {
    const storedParams = ephemeralLocalParams.get(callId);
    for (const key in storedParams) {
      if (key !== '_callId') {
        data[key] = storedParams[key];
      }
    }
    return;
  }

  // for regular pages (sessionStorage) - hydrate only local
  const localId = data.localId;
  if (typeof localId === 'string') {
    data.local = retrieveLocalParam(localId);
  }
}

/**
 * Evict oldest local parameter entries if count exceeds LOCAL_STORAGE_MAX_ENTRIES, then closed
 * pages' entries past CLOSED_PAGE_ENTRIES_BUDGET_CHARS.
 * Called before storing a new entry to make room.
 */
function evictOldestLocalParams(): void {
  const entries = readStoredEntries();

  // if at or over capacity, remove oldest to make room for the new entry
  if (entries.length >= LOCAL_STORAGE_MAX_ENTRIES) {
    entries.sort((a, b) => a.timestamp - b.timestamp);
    const toRemove = entries.length - LOCAL_STORAGE_MAX_ENTRIES + 1;
    for (let i = 0; i < toRemove; i++) {
      sessionStorage.removeItem(entries[i].key);
    }
    entries.splice(0, toRemove);
  }

  // keep closed pages' entries under their size budget (#6493)
  removeClosedPageEntries(entries, CLOSED_PAGE_ENTRIES_BUDGET_CHARS);
}

/** One stored entry, as the eviction passes need it. */
interface StoredEntrySummary {
  key: string;
  timestamp: number;
  // key and value together, which is what counts against the quota
  chars: number;
}

/**
 * Read every local-param entry in sessionStorage. An entry that cannot be parsed is removed and
 * reported rather than returned.
 */
function readStoredEntries(): StoredEntrySummary[] {
  const entries: StoredEntrySummary[] = [];

  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith(LOCAL_STORAGE_PREFIX)) {
      try {
        const stored = sessionStorage.getItem(key);
        if (stored) {
          const entry: LocalParamEntry = JSON.parse(stored);
          entries.push({ key, timestamp: entry.timestamp, chars: key.length + stored.length });
        }
      } catch (error) {
        // unparseable — remove, then report the repair
        sessionStorage.removeItem(key);
        settings.reportDamage(LocalParamDamage.Evicted, error);
      }
    }
  }
  return entries;
}

/**
 * Process parameters before navigation, extracting local params to sessionStorage.
 * The `local` object contains complex objects that can't be serialized to URL.
 * The entire `local` object is stored in sessionStorage under one ID, and that
 * ID is stored as `localId` in urlParams.
 */
export function processParamsForNavigation(params: any, pageId: string, ephemeralCallId?: string): {
  urlParams: any;
} {
  if (!params || typeof params !== 'object') {
    return { urlParams: params };
  }

  // for ephemeral callable pages, store ALL params in memory (only _callId goes in URL)
  if (ephemeralCallId) {
    ephemeralLocalParams.set(ephemeralCallId, params);
    return { urlParams: { _callId: params._callId } };
  }

  // for regular pages, process normally
  const urlParams: any = {};

  for (const [key, value] of Object.entries(params)) {
    if (key === 'local' && value && typeof value === 'object') {
      // store entire local object in sessionStorage under one ID
      const localId = storeLocalParam(value, pageId);
      urlParams.localId = localId;
    } else {
      urlParams[key] = value;
    }
  }

  return { urlParams };
}
